import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import { genericHeaders, serviceUrl } from "../../utils/apiConfig";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { setIsViewedEmployee } from "../../store/reducers/addEmployeeSlice";
import { useDispatch } from "react-redux";
import { useGrantProxyAdminMutation } from "../../api/tenantApi";

// Avatar helpers (mirroring ManageTenants for consistency)
const avatarColor = (str = "") => {
  const palette = [
    "bg-blue-primary",
    "bg-indigo-500",
    "bg-violet-500",
    "bg-rose-500",
    "bg-amber-500",
    "bg-emerald-500",
    "bg-teal-500",
    "bg-cyan-500",
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};
const initialsOf = (name = "") => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ROLE_STYLES = {
  ADMIN: { chip: "bg-violet-50 text-violet-700 border-violet-200", icon: "fas fa-user-tie" },
  PROXY_ADMIN: { chip: "bg-violet-50 text-violet-700 border-violet-200", icon: "fas fa-user-tie" },
  EMPLOYEE: { chip: "bg-blue-secondery-1 text-blue-primary border-blue-secondery", icon: "fas fa-user-graduate" },
  MANAGER: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: "fas fa-user-plus" },
};

const EmployeeData = () => {
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState({});
  const [selectedTenant, setSelectedTenant] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [employees, setEmployees] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const [department, setDepartment] = useState([]);
  const [designation, setDesignation] = useState([]);
  const [workLocation, setWorkLocation] = useState([]);
  const [gridLoader, setGridLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [grantProxyAdmin] = useGrantProxyAdminMutation();

  // Grant proxy admin confirmation
  const [grantConfirm, setGrantConfirm] = useState({ open: false, row: null });
  const [granting, setGranting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, error: false, message: "" });

  const getTenants = async () => {
    const url = `${serviceUrl}/api/tenant/tenants`;
    try {
      let response = await axios.get(url, { headers: genericHeaders() });
      if (response.status === 200) {
        let newData = response.data;
        setTenants(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getEmployees = async () => {
    setGridLoader(true);
    const url = `${serviceUrl}/api/super/admin/employees/search`;
    const body = {
      tenantId: selectedTenant ?? "",
      searchString: searchValue,
      page: paginationModel.page,
      size: paginationModel.pageSize,
    };
    try {
      let response = await axios.post(url, body, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        setGridLoader(false);
        let newData = response.data;
        setEmployees(newData);
      }
    } catch (err) {
      setGridLoader(false);
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getReportingManager = async () => {
    const url = `${serviceUrl}/api/super/admin/reporting/managers`;
    try {
      let response = await axios.get(url, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        let newData = response?.data?.data;
        setReportingManagers(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getDepartment = async () => {
    const url = `${serviceUrl}/api/department/super/admin/departments`;
    try {
      let response = await axios.get(url, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        let newData = response?.data?.data;
        setDepartment(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getDesignation = async () => {
    const url = `${serviceUrl}/api/designation/super/admin/designations`;
    try {
      let response = await axios.get(url, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        let newData = response?.data?.data;
        setDesignation(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  const getWorkLocation = async () => {
    const url = `${serviceUrl}/api/workLocation/super/admin/worklocations`;
    try {
      let response = await axios.get(url, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        let newData = response?.data?.data;
        setWorkLocation(newData);
      }
    } catch (err) {
      let blocker = { ...error };
      blocker.tenantName = "Something Went Wrong";
      setError(blocker);
      console.error(err);
    }
  };

  useEffect(() => {
    getEmployees();
  }, [selectedTenant, searchValue, paginationModel]);

  useEffect(() => {
    getTenants();
    getDepartment();
    getDesignation();
    getWorkLocation();
    getReportingManager();
          dispatch(setIsViewedEmployee(false));
  }, []);

  const handleEdit = (row) => {
    const country = row?.tenantCountry || "";
    dispatch(setIsViewedEmployee(true));
    if (country === "USA") {
      navigate(`/usaView/employee/${row.id}`);
    } else {
      navigate(`/view/employee/${row.id}`);
    }
  };

  const getReportingManagerName = (id) => {
    return reportingManagers.find((manager) => manager.id === id)?.name ?? "-";
  };

  const getDepartmentName = (id) => {
    return (
      department.find((department) => department._id === id)?.departmentName ??
      "-"
    );
  };
  const getDesignationName = (id) => {
    return (
      designation.find((designation) => designation._id === id)?.designation ??
      "-"
    );
  };

  const getWorkLocationName = (id) => {
    return (
      workLocation.find((workLocation) => workLocation._id === id)?.location ??
      "-"
    );
  };

  const selectedTenantLabel = useMemo(() => {
    if (!selectedTenant) return "All Tenants";
    const t = tenants?.data?.find((x) => x.tenantid === selectedTenant);
    return t?.name || "Selected tenant";
  }, [selectedTenant, tenants]);

  const baseColumns = [
    {
      field: "name",
      headerName: "Employee",
      flex: 1.6,
      minWidth: 220,
      renderCell: (params) => {
        const name = params?.row?.name || "—";
        const empId = params?.row?.employeeId || "—";
        return (
          <div className="flex items-center gap-3 py-1">
            <span className={`w-9 h-9 rounded-full text-white font-bold text-xs flex items-center justify-center shadow-sm shrink-0 ${avatarColor(name)}`}>
              {initialsOf(name)}
            </span>
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-semibold text-slate-800 truncate">{name}</span>
              <span className="text-[11px] text-gray-primary truncate">ID: {empId}</span>
            </div>
          </div>
        );
      },
    },
    {
      field: "professionalEmailId",
      headerName: "Email",
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <span className="text-slate-700 text-[13px] truncate" title={params?.row?.professionalEmailId}>
          {params?.row?.professionalEmailId ?? "-"}
        </span>
      ),
    },
    {
      field: "designation",
      headerName: "Designation",
      flex: 1.1,
      minWidth: 150,
      renderCell: (params) => (
        <span className="text-slate-700 text-[13px]">{getDesignationName(params?.row?.designation)}</span>
      ),
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <span className="text-slate-700 text-[13px]">{getDepartmentName(params?.row?.department)}</span>
      ),
    },
    {
      field: "reportingManager",
      headerName: "Reporting Manager",
      flex: 1.2,
      minWidth: 170,
      renderCell: (params) => (
        <span className="text-slate-700 text-[13px]">
          {getReportingManagerName(params?.row?.reportingManager) ?? "-"}
        </span>
      ),
    },
    {
      field: "workLocation",
      headerName: "Location",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <span className="text-slate-700 text-[13px]">{getWorkLocationName(params?.row?.workLocation)}</span>
      ),
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.9,
      minWidth: 130,
      renderCell: (params) => {
        const role = params?.row?.role || "EMPLOYEE";
        const style = ROLE_STYLES[role] || ROLE_STYLES.EMPLOYEE;
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${style.chip}`}>
            <i className={`${style.icon} text-[11px]`} />
            {role}
          </span>
        );
      },
    },
    {
      field: "edit",
      headerName: "View",
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View employee">
          <span
            className="cursor-pointer inline-flex items-center justify-center w-9 h-9 rounded-lg text-blue-primary bg-blue-secondery-2 hover:bg-blue-secondery-1 transition-colors"
            onClick={() => handleEdit(params.row)}
          >
            <VisibilityIcon fontSize="small" />
          </span>
        </Tooltip>
      ),
    },
  ];

  // Append "Assign Proxy Admin" column only when a specific tenant is selected
  const assignColumn = {
    field: "assignProxy",
    headerName: "Assign Proxy Admin",
    flex: 0.9,
    minWidth: 160,
    sortable: false,
    renderCell: (params) => {
      const role = params?.row?.role;
      const isAlreadyAdmin = role === "ADMIN" || role === "PROXY_ADMIN";
      return isAlreadyAdmin ? (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-50 text-violet-500 border border-violet-200 text-[11px] font-semibold">
          Already Admin
        </span>
      ) : (
        <Tooltip title="Grant Proxy Admin role">
          <button
            onClick={() => handleGrantConfirm(params.row)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            Assign
          </button>
        </Tooltip>
      );
    },
  };

  const columns = selectedTenant ? [...baseColumns, assignColumn] : baseColumns;

  // Keep getIcon for backward compatibility (no longer rendered in row)
  const getIcon = (user) => {
    let icon = `fad fa-check-circle`;
    switch (user) {
      case "ADMIN":
        icon = "fas fa-user-tie";
        break;
      case "EMPLOYEE":
        icon = "fas fa-user-graduate";
        break;
      case "MANAGER":
        icon = "fas fa-user-plus";
        break;
    }
    return (
      <div style={{ color: "#515151", fontSize: "17pt", margin: "0px 7px" }}>
        <Tooltip title={user}>
          <i className={icon} />
        </Tooltip>
      </div>
    );
  };

  const handleGrantConfirm = (row) => setGrantConfirm({ open: true, row });
  const handleGrantCancel = () => setGrantConfirm({ open: false, row: null });

  const handleGrantExecute = async () => {
    const { row } = grantConfirm;
    setGranting(true);
    setGrantConfirm({ open: false, row: null });
    try {
      await grantProxyAdmin({ tenantId: selectedTenant, employeeId: row.id }).unwrap();
      setSnackbar({ open: true, error: false, message: `${row.name} has been assigned as Proxy Admin` });
      await getEmployees(); // refresh the grid
    } catch {
      setSnackbar({ open: true, error: true, message: "Failed to assign Proxy Admin role" });
    } finally {
      setGranting(false);
    }
  };

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  };

  return (
    <div className="px-8 py-7 bg-blue-secondery-2 min-h-[calc(100vh-64px)] overflow-x-hidden">
      {/* ── Header ── */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-secondery-1 text-blue-primary text-[11px] font-bold uppercase tracking-wider mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-primary" />
            People
          </div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Employees Across Tenants</h1>
          <p className="text-sm text-gray-primary mt-1 max-w-2xl">
            Search and inspect every employee on the platform. Filter by tenant or search by name & email.
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-blue-secondery shadow-sm px-5 py-3 flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-blue-secondery-2 text-blue-primary flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          </span>
          <div className="leading-tight">
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-primary">Total</p>
            <p className="text-xl font-bold text-slate-800">{employees?.totalcount || 0}</p>
          </div>
        </div>
      </div>

      {/* ── Filters Card ── */}
      <div className="bg-white rounded-2xl border border-blue-secondery shadow-sm p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-[10px] font-bold text-gray-primary uppercase tracking-wider mb-1.5">Search</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-primary pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
              </span>
              <input
                type="text"
                placeholder="Search by name or professional email…"
                className="w-full border border-blue-secondery bg-blue-secondery-2 rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary placeholder:text-gray-primary transition-colors"
                value={searchValue}
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className="w-full md:w-[300px]">
            <label className="block text-[10px] font-bold text-gray-primary uppercase tracking-wider mb-1.5">Tenant</label>
            <select
              value={selectedTenant}
              onChange={(e) => {
                setPaginationModel({
                  page: 0,
                  pageSize: 10,
                })
                setSelectedTenant(e.target.value)
              }}
              className="w-full border border-blue-secondery bg-blue-secondery-2 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary cursor-pointer transition-colors"
            >
              <option value={""}>All Tenants</option>
              {tenants?.data?.map((tenant) => {
                return (
                  <option key={tenant.id} value={tenant.tenantid}>
                    {tenant.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Active filter chips */}
        {(searchValue || selectedTenant) && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-blue-secondery-1">
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-primary">Active filters:</span>
            {searchValue && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-secondery-1 text-blue-primary text-xs font-semibold">
                Search: "{searchValue}"
                <button
                  type="button"
                  onClick={() => { setSearchValue(""); setPaginationModel({ page: 0, pageSize: 10 }); }}
                  className="hover:bg-white/60 rounded-full w-4 h-4 inline-flex items-center justify-center"
                  title="Clear search"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            )}
            {selectedTenant && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-secondery-1 text-blue-primary text-xs font-semibold">
                Tenant: {selectedTenantLabel}
                <button
                  type="button"
                  onClick={() => { setSelectedTenant(""); setPaginationModel({ page: 0, pageSize: 10 }); }}
                  className="hover:bg-white/60 rounded-full w-4 h-4 inline-flex items-center justify-center"
                  title="Clear tenant"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Data Grid ── */}
      <div className="bg-white rounded-2xl border border-blue-secondery shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-blue-secondery-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-blue-primary text-white flex items-center justify-center shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" /></svg>
            </span>
            <div>
              <h2 className="text-base font-bold text-slate-800 leading-tight">Employee Directory</h2>
              <p className="text-[11px] text-gray-primary">Showing {selectedTenantLabel}</p>
            </div>
          </div>
        </div>
        <div style={{ minHeight: 540, width: "100%", overflow: "hidden" }}>
          <DataGrid
            rows={employees?.data || []}
            columns={columns}
            loading={gridLoader}
            disableColumnMenu
            rowHeight={64}
            onPaginationModelChange={(newPaginationModel) =>
              setPaginationModel(newPaginationModel)
            }
            paginationModel={paginationModel}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 20, 50, 75, 100]}
            paginationMode="server"
            rowCount={employees?.totalcount || 0}
            sx={{
              border: 0,
              fontFamily: "inherit",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F0F9FF",
                borderBottom: "1px solid #DBEAFE",
                color: "#71717A",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #DBEAFE", color: "#334155", display: "flex", alignItems: "center" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "#F0F9FF" },
              "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #DBEAFE", backgroundColor: "#F0F9FF" },
              "& .MuiDataGrid-virtualScroller": { backgroundColor: "#fff" },
              "& .MuiDataGrid-overlay": { backgroundColor: "rgba(240, 249, 255, 0.6)" },
            }}
          />
        </div>
      </div>
      {/* ── Grant Proxy Admin confirmation dialog ── */}
      <Dialog open={grantConfirm.open} onClose={handleGrantCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Proxy Admin</DialogTitle>
        <DialogContent>
          {grantConfirm.row && (
            <p className="text-sm text-gray-600">
              Assign{" "}
              <span className="font-semibold text-gray-800">{grantConfirm.row.name}</span>
              {" "}as a Proxy Admin for this tenant? Their role will be updated to Admin.
            </p>
          )}
        </DialogContent>
        <DialogActions className="px-4 pb-3">
          <Button onClick={handleGrantCancel} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleGrantExecute}
            variant="contained"
            size="small"
            disabled={granting}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, error: false, message: "" })}
      >
        <Alert
          severity={snackbar.error ? "error" : "success"}
          variant="filled"
          onClose={() => setSnackbar({ open: false, error: false, message: "" })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EmployeeData;
